<template>
	<div ref="el" data-component="timetable">
		<div class="row align-center days-row">
			<div class="xxlarge-16 col days-container">
				<DraggableList :have-handler="false" ref="dragDaysList">
					<ul class="list">
						<li v-for="(item, i) in computedDataRef" :key="i" :data-index="i" class="item" @click="handleDayClick">
							<TagButton :label="item?.day" :active-state="i === activeDay" />
						</li>
					</ul>
				</DraggableList>
			</div>
		</div>

		<div class="row align-center timetable-row">
			<div class="xxlarge-12 medium-15 col">
				<div class="hours-handler">
					<span class="line" />
					<span class="-label-2 -uppercase label">{{ $t('now', 'Agora') }}</span>
				</div>
				<div class="hours-handler right">
					<span class="line" />
					<span class="-label-2 -uppercase label">{{ $t('now', 'Agora') }}</span>
				</div>

				<div class="table-container">
					<div
						v-for="(item, i) in computedDataRef"
						:key="i"
						:data-index="i"
						:data-day="item.day"
						:data-club="item.grouped.nome_clube[0]"
						:class="`day-table-container  ${activeDay === i ? 'active' : ''}`"
					>
						<div class="lines-overlay">
							<span v-for="(_, i) in item.grouped.class_date" :data-row="i" class="row-line" />
						</div>

						<div class="drag-overlay-gradient">
							<span v-for="(_, i) in item.grouped.class_date" :data-row="i" class="row-line" />
						</div>

						<div class="fixed-cols">
							<div class="table-header">
								<span class="-label-2 -uppercase icons-col"></span>
								<span class="-label-2 -uppercase classes-col">{{ $t('class', 'Aula') }}</span>
							</div>
							<div class="columns-container">
								<div class="icons-col table-col">
									<div
										v-for="(name, i) in item.grouped.nome_turma"
										:key="i"
										:data-row="i"
										:data-start="formatSchedule(item.grouped.hrstart[i])"
										:data-end="formatSchedule(item.grouped.hrend[i])"
										class="icon-container table-cell"
										:style="getColor(name) ? `--class-color: ${getColor(name)}` : ''"
									>
										<SvgAsset v-if="getIcon(name)" :url="getIcon(name)" />
										<Icon v-else-if="classesSlugs.includes(convertToSlug(name))" :slug="convertToSlug(name)" :is-class="true" :class-circle="true" />
										<span v-else class="no-class-icon">
											<SvgAsset url="/svg/logo.svg" />
										</span>
									</div>
								</div>

								<div class="classes-col table-col">
									<div v-for="(name, i) in item.grouped.nome_turma" :data-row="i" :key="i" class="class-container table-cell">
										<HyperLink v-if="getClass(name)" :to="getClass(name)" class="link">
											<span class="-title-6 -uppercase label">{{ name }}</span>
										</HyperLink>
										<span v-else class="-title-6 -uppercase label">{{ name }}</span>
									</div>
								</div>
							</div>
						</div>

						<div class="draggable-cols">
							<DraggableList :have-handler="false" ref="dragList" class="drag-list">
								<div class="inner">
									<div class="table-header">
										<span class="-label-2 -uppercase schedules-col">{{ $t('schedule', 'Horário') }}</span>
										<span class="-label-2 -uppercase durations-col">{{ $t('duration', 'Duração') }}</span>
										<span class="-label-2 -uppercase studios-col">{{ $t('studio', 'Estúdio') }}</span>
										<span class="-label-2 -uppercase instructors-col">{{ $t('instructor', 'Instrutor') }}</span>
									</div>

									<div class="columns-container">
										<div class="schedules-col table-col">
											<div v-for="(hrstart, i) in item.grouped.hrstart" :data-row="i" :key="i" class="schedule-container table-cell">
												<span class="-label-1 -uppercase start-date">{{ formatSchedule(hrstart) }}</span>
												<span class="-label-1 -uppercase end-date"> <span class="separator">—</span> {{ formatSchedule(item.grouped.hrend[i]) }}</span>
											</div>
										</div>

										<div class="durations-col table-col">
											<div v-for="(hrstart, i) in item.grouped.hrstart" :data-row="i" :key="i" class="duration-container table-cell">
												<span class="-label-1 -uppercase label">{{ getDuration(hrstart, item.grouped.hrend[i]) }}</span>
											</div>
										</div>

										<div class="studios-col table-col">
											<div v-for="(nome_instalacao, i) in item.grouped.nome_instalacao" :data-row="i" :key="i" class="studios-container table-cell">
												<span class="-label-1 -uppercase label">{{ nome_instalacao }}</span>
											</div>
										</div>

										<div class="instructors-col table-col">
											<div v-for="(nome_responsavel, i) in item.grouped.nome_responsavel" :data-row="i" :key="i" class="instructor-container table-cell">
												<span class="-label-1 -uppercase label">{{ nome_responsavel }}</span>
											</div>
										</div>
									</div>
								</div>
							</DraggableList>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { gsap } from 'gsap/all'

import { Icon, TagButton } from '~/components/primitives'
import { DraggableList, HyperLink, SvgAsset } from '~/components/core'

import { convertToSlug, removeSpacesAndAccents } from '~/utils/strings'
import { classesSlugs } from '~/config'
import { useStringTranslation, useLocale } from '~/i18n'

import { useEnterAnimation } from '~/composables/core'

import { useScreen } from 'vue-screen'

const props = defineProps({
	api: { type: String, default: '' },
	classes: { type: Array, default: () => [] },
	animationDelay: { type: Number, default: 0 },
})
const context = inject('pageContext')
const scroller = inject('$scroller')
const screen = useScreen()

const locale = useLocale()
const $t = useStringTranslation()
const el = ref(null)
const dragList = ref(null)
const dragDaysList = ref(null)
const activeDay = ref(0)
const enterAnim = useEnterAnimation()
const tl = gsap.timeline({ paused: true })

const defineActiveDay = () => {
	const today = new Date().getDay()
	activeDay.value = today === 0 ? 6 : today - 1

	el.value.querySelectorAll('.day-table-container')[activeDay.value].classList.add('current-day')
}

watch(screen, () => defineActiveHoursHandler())
const defineActiveHoursHandler = () => {
	if (!el.value) return
	const today = new Date().getDay()
	const offsetToday = today === 0 ? 6 : today - 1
	const hoursHandler = el.value.querySelectorAll('.hours-handler')
	const linesOverlay = el.value.querySelectorAll('.lines-overlay')
	const dragOverlay = el.value.querySelectorAll('.drag-overlay-gradient')
	const activeTimetableDayRows = el.value.querySelectorAll('.day-table-container.active .icon-container')
	const inRangeRows = []
	const tableHeaderHeight = 40

	if (offsetToday !== activeDay.value) {
		gsap.set(hoursHandler, {
			display: 'none',
			opacity: 0,
		})
		linesOverlay.forEach((el) => el.classList.remove('add-color-line'))
		dragOverlay.forEach((el) => el.classList.remove('add-color-line'))
		return
	}

	activeTimetableDayRows.forEach((row) => {
		if (isCurrentTimeWithinInterval(row.dataset.start, row.dataset.end)) {
			inRangeRows.push(row)

			const rowIndex = row.dataset.row
			const activeRows = el.value.querySelectorAll(`.lines-overlay [data-row="${rowIndex}"], .drag-overlay-gradient [data-row="${rowIndex}"]`)

			activeRows.forEach((el) => el.classList.add('active-row'))
		}
	})

	if (inRangeRows.length) {
		// Set the height and position of the handler based on the InRange array
		const rowHeight = inRangeRows[0].getBoundingClientRect().height + 1
		const rowTop = inRangeRows[0].offsetTop
		const numRows = inRangeRows.length

		gsap.set(hoursHandler, {
			height: rowHeight * numRows,
			top: rowTop + tableHeaderHeight - 1,
			display: 'flex',
			opacity: 1,
		})

		linesOverlay.forEach((el) => el.classList.add('add-color-line'))
		dragOverlay.forEach((el) => el.classList.add('add-color-line'))
	} else {
		gsap.set(hoursHandler, {
			display: 'none',
			opacity: 0,
		})
		linesOverlay.forEach((el) => el.classList.remove('add-color-line'))
		dragOverlay.forEach((el) => el.classList.remove('add-color-line'))
	}
}

function isWithinRange(time, range = 30) {
	// Get the current date and time
	const now = new Date()

	// Parse the provided time string ("HH:mm") into a Date object
	const [hours, minutes] = time.split(':').map(Number)
	const targetTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes)

	// Calculate 0 minutes back and 60 minutes forward
	const minutesBack = new Date(now.getTime() - range * 0 * 1000)
	const minutesForward = new Date(now.getTime() + range * 60 * 1000)

	// Check if the target time is within the range
	return targetTime >= minutesBack && targetTime <= minutesForward
}

function isCurrentTimeWithinInterval(startTime, endTime) {
	// Parse the current time in HH:mm format
	const now = new Date()
	const currentMinutes = now.getHours() * 60 + now.getMinutes()

	// Convert start and end times to minutes since midnight
	const [startHours, startMinutes] = startTime.split(':').map(Number)
	const startTotalMinutes = startHours * 60 + startMinutes

	const [endHours, endMinutes] = endTime.split(':').map(Number)
	const endTotalMinutes = endHours * 60 + endMinutes

	if (startTotalMinutes <= endTotalMinutes) {
		// Regular interval, not spanning midnight
		return currentMinutes >= startTotalMinutes && currentMinutes <= endTotalMinutes
	} else {
		// Interval spans midnight
		return currentMinutes >= startTotalMinutes || currentMinutes <= endTotalMinutes
	}
}

const weekdays = [$t('monday', 'Segunda'), $t('tuesday', 'Terça'), $t('wednesday', 'Quarta'), $t('thursday', 'Quinta'), $t('friday', 'Sexta'), $t('saturday', 'Sabado'), $t('sunday', 'Domingo')]

const { data } = useAsyncData(props.api, () => $fetch(props.api), {
	getCachedData: () => undefined,
})

watch(
	data,
	(newData) => {
		if (newData) {
			nextTick(() => {
				computedDataRef.value = computeData()
				nextTick(() => {
					setup()
					setTimeout(() => refresh(), 100)
				})
			})
		}
	},
	{ immediate: true }, // Run immediately if data is already available
)

const init = () => {
	watch(
		computedDataRef,
		() => {
			if (computedDataRef.value.length === 0) return
			nextTick(() => {
				tl.fromTo(
					el.value.querySelectorAll('.days-container .item'),
					{
						opacity: 0,
						y: 40,
					},
					{
						opacity: 1,
						y: 0,
						stagger: {
							from: 'center',
							each: 0.1,
						},
						delay: props.animationDelay,
						ease: 'power4.out',
						duration: 1,
					},
				)
				tl.fromTo(
					el.value.querySelector('.timetable-row'),
					{
						opacity: 0,
						y: 60,
					},
					{
						opacity: 1,
						y: 0,
						delay: props.animationDelay,
						ease: 'power4.out',
						duration: 2,
					},
					0,
				)

				if (el.value.classList.contains('active')) {
					nextTick(() => {
						enterAnim.init({
							trigger: el.value,
							timeline: tl,
							start: props.animationDelay === 0 ? 'top+=200px bottom' : 'top bottom',
						})
					})
				}
			})
		},
		{ immediate: true },
	)
}

// Function to parse time string into Date object (using today's date)
function parseTime(timeStr) {
	const [hours, minutes, seconds] = timeStr.split(':')
	const [sec, ms] = seconds.split('.')
	const now = new Date()
	return new Date(now.setHours(hours, minutes, sec, ms))
}

const getDuration = (start, end) => {
	const date1 = parseTime(start)
	const date2 = parseTime(end)
	const diff = date2 - date1

	// Convert milliseconds to hours, minutes, seconds, and milliseconds
	const msInHour = 3600000
	const msInMinute = 60000
	const msInSecond = 1000

	const hours = Math.floor(diff / msInHour)
	const minutes = Math.floor((diff % msInHour) / msInMinute)
	const seconds = Math.floor((diff % msInMinute) / msInSecond)
	const milliseconds = diff % msInSecond
	let string = ''

	if (hours) string += `${hours} H`
	if (hours && minutes) string += ` ${minutes} MIN`
	if (!hours && minutes) string += `${minutes} MIN`
	return string
}
const formatSchedule = (time) => {
	return `${time.split(':')[0]}:${time.split(':')[1]}`
}

const computedDataRef = ref([])
const computeData = () => {
	const aux = data.value.reduce((groups, el) => {
		// Transform Weekday from Sunday-Monday to Monday-Sunday
		const weekdayIndex = Number(el.class_weekday) === 1 ? 6 : Number(el.class_weekday) - 2
		const weekday = weekdays[weekdayIndex]

		if (!groups[weekdayIndex]) {
			groups[weekdayIndex] = {}
		}

		if (!groups[weekdayIndex]?.classes) {
			groups[weekdayIndex].classes = []
		}

		groups[weekdayIndex].day = weekday
		el.nome_turma !== 'COACH' && el.nome_turma !== 'GERAL' && groups[weekdayIndex].classes.push(el)

		return groups
	}, [])

	weekdays.forEach((element, i) => {
		let exists = false
		aux.forEach((el) => {
			if (el.day === element) {
				exists = true
			}
		})

		if (!exists) {
			aux.splice(i, 0, {
				classes: [],
				day: element,
			})
		}
	})

	const transformedArray = aux
		.filter((dayObj) => dayObj !== undefined)
		.map((dayObj) => {
			const { day, classes } = dayObj

			if (classes.length) {
				// Sort the `classes` array by `hrstart`
				const sortedClasses = classes.sort((a, b) => {
					const timeA = a.hrstart
					const timeB = b.hrstart
					return timeA.localeCompare(timeB) // Compare time strings
				})

				// Get all keys from the first class object dynamically
				const keys = Object.keys(sortedClasses[0])

				// Dynamically group values by key
				const groupedData = keys.reduce((acc, key) => {
					if (!key.includes('cct') && key !== '_ID' && key !== 'class_weekday') {
						acc[key] = classes.map((cls) => cls[key])
					}
					return acc
				}, {})

				return {
					day: day.toLowerCase(),
					grouped: groupedData,
				}
			} else {
				return {
					day: day.toLowerCase(),
					grouped: {
						nome_turma: [],
						hrstart: [],
						hrend: [],
						nome_instalacao: [],
						nome_responsavel: [],
						nome_clube: [],
					},
				}
			}
		})

	// aux.forEach((el) => el.classes.sort((a, b) => a.hrstart.localeCompare(b.hrstart)))
	// const final = aux.filter((item) => item !== null && item !== undefined && item !== '')

	return transformedArray
}

const getIcon = (name) => props.classes.find((el) => el.title.toLowerCase() === name.toLowerCase())?.icon?.url
const getColor = (name) => props.classes.find((el) => removeSpacesAndAccents(el.title).toLowerCase() === removeSpacesAndAccents(name).toLowerCase())?.color

const getClass = (name) => {
	const ptClass = props.classes.find((el) => removeSpacesAndAccents(el.title).toLowerCase() === removeSpacesAndAccents(name).toLowerCase())
	if (ptClass) return locale.value === 'pt' ? ptClass?.link : ptClass?.translation[0]?.linkEn ? ptClass?.translation[0]?.linkEn : null
	else return null
}

const handleDayClick = (elem) => {
	const targetIndex = Number(elem.target.closest('.item').dataset.index)
	const targetTable = el.value.querySelectorAll('.day-table-container')[targetIndex]
	const currentTable = el.value.querySelectorAll('.day-table-container')[activeDay.value]

	currentTable.classList.remove('active')
	targetTable.classList.add('active')

	activeDay.value = targetIndex

	setTimeout(() => {
		scroller.value.resize()
		defineActiveHoursHandler()

		dragList.value && dragList.value[activeDay.value]?.reload()
	}, 10)
}

const animateIn = () => tl.seek(0).play()
const refresh = () => {
	defineActiveDay()

	setTimeout(() => {
		dragList.value && dragList.value[activeDay.value]?.reload()
		dragDaysList.value?.reload()

		defineActiveHoursHandler()
	}, 100)
}

const setup = () => {
	if (!el.value) return
	gsap.set([el.value.querySelectorAll('.days-container .item'), el.value.querySelectorAll('.timetable-row')], { opacity: 0 })
}

const kill = () => {
	dragList.value && dragList.value[activeDay.value]?.kill()
	dragDaysList.value?.kill()
}

onBeforeUnmount(() => kill())

onMounted(() => {
	context.$page.loader.ready.then(() => init())
})

defineExpose({
	refresh,
	kill,
	animateIn,
})
</script>

<style lang="scss" scoped>
[data-component='timetable'] {
	position: relative;
	padding-top: rs(48);

	display: flex;
	flex-direction: column;
	gap: rs(68);
	width: 100%;

	opacity: 0;

	.col {
		position: relative;
		padding: 0;
	}

	.timetable-row,
	.days-row {
		position: relative;

		overflow: hidden;

		[data-component='draggable-list'] {
			overflow: visible;
		}
	}

	.hours-handler {
		position: absolute;
		top: 0;
		left: 2px;

		transform: translateX(-100%);

		display: flex;
		align-items: center;
		padding-right: rs(10);

		border-right: 4px solid var(--color-yellow);
		color: var(--color-yellow);

		z-index: 2;

		opacity: 0;

		&.right {
			left: initial;
			right: 2px;
			padding-right: 0;
			padding-left: rs(10);
			transform: translateX(100%);

			border-left: 4px solid var(--color-yellow);
			border-right: initial;

			@include max-screen($small) {
				display: none;
			}

			.label {
				order: 2;
			}
		}

		@include max-screen($medium) {
			padding-right: 0;

			.label {
				position: relative;
				left: rs(6);
				transform: rotate(-90deg);
			}
		}
	}

	.days-container {
		position: relative;

		width: 100%;

		.list {
			display: flex;
			justify-content: center;
			width: max-content;
			min-width: 100%;
			gap: rs(6);
		}
	}

	.lines-overlay {
		position: absolute;
		top: rs(40, 40);
		left: 0;
		display: flex;
		flex-direction: column;

		height: 100%;
		width: 100%;

		background-color: var(--color-warmGrey);

		pointer-events: none;

		&.add-color-line {
			.row-line {
				&.active-row {
					background-color: #fff6b2;
				}
			}
		}

		.row-line {
			position: relative;
			height: rs(92, 48);
			width: 100%;

			z-index: 2;
		}
	}

	.drag-overlay-gradient {
		position: absolute;
		top: rs(40, 40);
		left: 0;
		display: flex;
		flex-direction: column;

		height: 100%;
		width: 100%;

		z-index: 10;
		pointer-events: none;

		&.add-color-line {
			.active-row {
				&:before {
					background: linear-gradient(90deg, rgba(#fff6b2, 1) 10%, rgba(243, 239, 215, 0) 100%);
				}
			}
		}

		.row-line {
			position: relative;
			height: rs(92, 48);
			width: 100%;

			z-index: 2;

			border-bottom: 1px solid #ded7b2;

			&:before {
				content: '';
				position: absolute;
				top: 0;
				left: calc(#{n_grid_columns(7)});

				display: block;
				height: 100%;
				width: calc(var(--grid-gutter) * 10);

				background: linear-gradient(90deg, rgba(243, 239, 215, 1) 10%, rgba(243, 239, 215, 0) 100%);
			}
		}
	}

	.table-container {
		position: relative;

		display: grid;
	}

	.link {
		position: relative;

		@media (hover: hover) {
			&:hover {
				span {
					font-variation-settings:
						'wdth' 0,
						'wght' 550;
				}
			}
		}
		span {
			transition: font-variation-settings 0.25s ease(out-expo);
		}
	}

	.table-header {
		position: relative;

		display: flex;
		align-items: center;
		width: 100%;
		height: rs(40, 40);
		padding: rs(10) 0 rs(10) var(--grid-gutter);

		gap: calc(var(--grid-gutter) * 2);
		color: rgba(from var(--color-warmGrey) r g b / 0.4);

		&:before {
			content: '';
			position: absolute;
			top: 0;
			left: calc(var(--grid-gutter) * -2);

			display: block;
			width: calc(100% + var(--grid-gutter) * 4);
			height: 100%;
		}
	}

	.day-table-container {
		grid-row: 1;
		grid-column: 1;

		display: none;
		overflow: hidden;

		color: var(--color-darkBrown);

		&.active {
			display: flex;
			gap: calc(var(--grid-gutter) * 2);
		}

		.columns-container {
			position: relative;
			display: flex;
			gap: calc(var(--grid-gutter) * 2);

			// &:before {
			// 	content: '';
			// 	position: absolute;
			// 	top: 0;
			// 	left: calc(var(--grid-gutter) * -2);

			// 	display: block;
			// 	background-color: var(--color-warmGrey);
			// 	width: calc(100% + var(--grid-gutter) * 4);
			// 	height: 100%;
			// }
		}

		.fixed-cols {
			position: relative;

			z-index: 2;

			// @include max-screen($small) {
			// 	z-index: 2;
			// }
		}

		.draggable-cols {
			position: relative;

			z-index: 2;

			@include max-screen($small) {
				overflow: hidden;
			}

			&:before {
				content: '';
				position: absolute;
				top: 0;
				left: 0%;

				display: none;
				width: 10%;
				height: rs(40);

				background: linear-gradient(90deg, rgba(22, 16, 3, 1) 0%, rgba(22, 16, 3, 0) 100%);

				z-index: 2;

				@include max-screen($xsmall) {
					display: block;
				}
			}

			&:after {
				content: '';
				position: absolute;
				top: rs(40, 40);
				left: 0%;

				display: none;
				width: 15%;
				height: 100%;

				background: linear-gradient(90deg, rgba(243, 239, 215, 1) 10%, rgba(243, 239, 215, 0) 100%);
				z-index: 2;

				opacity: 0;

				@include max-screen($xsmall) {
					display: block;
				}
			}

			.drag-list {
				z-index: 1;
			}

			.inner {
				width: 100%;

				z-index: 0;

				will-change: transform;

				@include max-screen($xsmall) {
					gap: rs(24);
					width: max-content;
				}
			}
		}

		.table-col {
			position: relative;

			.table-cell {
				position: relative;
				display: flex;
				align-items: center;
				height: rs(92, 48);

				padding-left: var(--grid-gutter);

				span {
					position: relative;
				}

				// &:before {
				// 	content: '';
				// 	position: absolute;
				// 	top: 0;
				// 	left: calc(var(--grid-gutter) * -2);

				// 	display: block;
				// 	width: calc(100% + var(--grid-gutter) * 4);
				// 	height: 100%;

				// 	border-bottom: 1px solid #ded7b2;
				// }

				// &.active-row {
				// 	&:before {
				// 		background-color: #fff6b2;
				// 	}
				// }
			}
		}

		.icons-col {
			position: relative;
			flex: 0 0 calc(#{n_grid_columns(1)});
			width: calc(#{n_grid_columns(1)});

			@include max-screen($xsmall) {
				flex: 0 0 n_grid_columns(2);
				width: n_grid_columns(2);
			}

			.no-class-icon,
			:deep([data-component='svg-asset']),
			:deep([data-component='icon']) {
				position: relative;

				display: flex;
				align-items: center;
				justify-content: center;
				width: rs(48);
				height: rs(48);

				background-color: var(--class-color, var(--color-darkBrown));
				border-radius: 100%;
			}

			.no-class-icon {
				background-color: var(--color-yellow);
				@include title-6;
				letter-spacing: -0.1em;

				border-radius: 100%;
				overflow: hidden;

				:deep([data-component='svg-asset']) {
					background-color: var(--color-yellow);
				}

				:deep(svg) {
					width: 60%;
				}
			}

			:deep(svg) {
				width: 100%;
				height: 100%;

				circle,
				.circle,
				.st1,
				path[fill='#221f1f'] {
					display: none;
				}
			}
		}

		.classes-col {
			position: relative;
			flex: 0 0 n_grid_columns(3);
			width: n_grid_columns(3);

			@include max-screen($small) {
				flex: 0 0 n_grid_columns(4);
				width: n_grid_columns(4);
			}

			@include max-screen($xsmall) {
				flex: 0 0 n_grid_columns(5);
				width: n_grid_columns(5);
			}
		}

		.schedules-col {
			position: relative;
			flex: 0 0 n_grid_columns(2);
			width: n_grid_columns(2);

			@include max-screen($medium) {
				flex: 0 0 n_grid_columns(3);
				width: n_grid_columns(3);
			}

			@include max-screen($xsmall) {
				flex: 0 0 n_grid_columns(3);
				width: n_grid_columns(3);
				text-align: center;
			}

			.end-date {
				opacity: 0.5;
			}

			.separator {
				@include max-screen($xsmall) {
					display: none;
				}
			}

			.schedule-container {
				@include max-screen($xsmall) {
					display: flex;
					flex-direction: column;
					justify-content: center;
					gap: rs(4);
				}
			}
		}

		.durations-col {
			position: relative;
			flex: 0 0 n_grid_columns(2);
			width: n_grid_columns(2);

			@include max-screen($xsmall) {
				flex: 0 0 n_grid_columns(3);
				width: n_grid_columns(3);
			}
		}

		.studios-col {
			position: relative;
			flex: 0 0 n_grid_columns(2);
			width: n_grid_columns(2);

			@include max-screen($medium) {
				flex: 0 0 n_grid_columns(3);
				width: n_grid_columns(3);
			}

			@include max-screen($small) {
				flex: 0 0 n_grid_columns(2);
				width: n_grid_columns(2);
			}

			@include max-screen($xsmall) {
				flex: 0 0 n_grid_columns(5);
				width: n_grid_columns(5);
			}
		}

		.instructors-col {
			position: relative;
			flex: 0 0 n_grid_columns(2);
			width: n_grid_columns(2);

			@include max-screen($medium) {
				flex: 0 0 n_grid_columns(3);
				width: n_grid_columns(3);
			}

			@include max-screen($xsmall) {
				flex: 1;
				width: auto;
				max-width: n_grid_columns(6);
			}

			.instructor-container {
				padding-right: rs(20);
			}

			.label {
				line-height: 1.2em;
			}
		}
	}
}
</style>
